import React, { useEffect, useRef } from 'react';
import { select } from 'd3-selection';

import { transformToOrientation } from '../utils';
import { defaultProps, propTypes } from './propTypes';
import DefaultNodeElement from './DefaultNodeElement';

const calculateTransform = (position, parent, orientation, shouldTranslateToOrigin = false) => {
  if (shouldTranslateToOrigin) {
    const hasParent = parent !== null && parent !== undefined;
    const origin = hasParent ? parent : { x: 0, y: 0 };
    const { x: tX, y: tY } = transformToOrientation(origin, orientation);
    return `translate(${tX},${tY})`;
  }
  const { x: tX, y: tY } = transformToOrientation(position, orientation);
  return `translate(${tX},${tY})`;
};
const applyTransform = (nodeRef, transform, transitionDuration, enableLegacyTransitions, opacity = 1) => {
  if (enableLegacyTransitions) {
    select(nodeRef).transition().duration(transitionDuration).attr('transform', transform).style('opacity', opacity);
  } else {
    select(nodeRef).attr('transform', transform).style('opacity', opacity);
  }
};

function Node({
  orientation,
  transitionDuration,
  position,
  parent,
  enableLegacyTransitions,
  onNodeToggle,
  onNodeClick,
  onNodeMouseOver,
  onNodeMouseOut,
  renderCustomNodeElement,
  nodeClassName,
  data,
}) {
  const nodeRef = useRef(null);

  useEffect(() => {
    let newTransform = calculateTransform(position, parent, orientation);
    applyTransform(nodeRef.current, newTransform, transitionDuration, enableLegacyTransitions);
    return () => {
      newTransform = calculateTransform(position, parent, orientation, true);
      applyTransform(nodeRef.current, newTransform, transitionDuration, enableLegacyTransitions, 0);
    };
  }, [enableLegacyTransitions, orientation, parent, position, transitionDuration]);

  const handleNodeToggle = () => onNodeToggle(data.__rd3t.id);

  const handleOnClick = (...args) => {
    onNodeClick(data.__rd3t.id, ...args);
  };

  const handleOnMouseOver = (...args) => {
    onNodeMouseOver(data.__rd3t.id, ...args);
  };

  const handleOnMouseOut = (...args) => {
    onNodeMouseOut(data.__rd3t.id, ...args);
  };

  const renderNodeElement = () => {
    const itemProps = {
      nodeDatum: data,
      toggleNode: handleNodeToggle,
      onNodeClick: handleOnClick,
      onNodeMouseOver: handleOnMouseOver,
      onNodeMouseOut: handleOnMouseOut,
    };
    if (typeof renderCustomNodeElement === 'function') {
      return renderCustomNodeElement({ ...itemProps });
    }
    return DefaultNodeElement({ ...itemProps });
  };

  const initialTransform = calculateTransform(position, parent, orientation, true);

  return (
    <g
      id={data.__rd3t.id}
      ref={(n) => {
        nodeRef.current = n;
      }}
      style={{
        opacity: 0,
      }}
      className={[data.children ? 'rd3t-node' : 'rd3t-leaf-node', nodeClassName].join(' ').trim()}
      transform={initialTransform}>
      {renderNodeElement()}
    </g>
  );
}

Node.propTypes = propTypes;
Node.defaultProps = defaultProps;

export default React.memo(Node);
